import { AccountInfo, IPublicClientApplication, SilentRequest } from '@azure/msal-browser';
import cookies from 'js-cookie';
import logger from './logger';

let inFlightEnsurePromise: Promise<boolean> | null = null;
export const LOGIN_ACCESS_TOKEN_COOKIE = 'loginAccessToken';
export const LOGIN_ACCESS_TOKEN_PRESENCE_COOKIE = 'loginAccessTokenPresence';

const cookieOptions = {
  path: '/',
  secure: process.env.NODE_ENV === 'production',
  sameSite: process.env.NODE_ENV === 'production' ? 'none' as const : 'lax' as const
};

const setPresenceCookie = (): void => {
  cookies.set(LOGIN_ACCESS_TOKEN_PRESENCE_COOKIE, '1', cookieOptions);
};

const removeAuthCookies = (): void => {
  cookies.remove(LOGIN_ACCESS_TOKEN_COOKIE, { path: '/' });
  cookies.remove(LOGIN_ACCESS_TOKEN_PRESENCE_COOKIE, { path: '/' });
};

export const hasLoginAccessTokenCookie = (): boolean => {
  const tokenValue = cookies.get(LOGIN_ACCESS_TOKEN_COOKIE);
  if (tokenValue !== undefined && tokenValue !== null && tokenValue !== '') {
    return true;
  }

  const presenceValue = cookies.get(LOGIN_ACCESS_TOKEN_PRESENCE_COOKIE);
  return presenceValue !== undefined && presenceValue !== null && presenceValue !== '';
};

export const waitForLoginAccessTokenCookie = async (timeoutMs = 5000, intervalMs = 50): Promise<boolean> => {
  const waitUntil = Date.now() + timeoutMs;

  while (Date.now() < waitUntil) {
    if (hasLoginAccessTokenCookie()) {
      return true;
    }

    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }

  return hasLoginAccessTokenCookie();
};

interface EnsureCookieParams {
  instance: IPublicClientApplication;
  loginRequest: SilentRequest;
  account?: AccountInfo | null;
  allowPresenceCookie?: boolean;
}

export const ensureLoginAccessTokenCookie = async ({
  instance,
  loginRequest,
  account,
  allowPresenceCookie = true
}: EnsureCookieParams): Promise<boolean> => {
  if (hasLoginAccessTokenCookie()) {
    return true;
  }

  if (inFlightEnsurePromise != null) {
    return await inFlightEnsurePromise;
  }

  inFlightEnsurePromise = (async () => {
    const resolvedAccount = account ?? instance.getAllAccounts()[0] ?? null;

    if (resolvedAccount == null) {
      removeAuthCookies();
      return false;
    }

    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: resolvedAccount
      });

      const token = tokenResponse?.accessToken ?? '';
      if (token !== '') {
        cookies.set(LOGIN_ACCESS_TOKEN_COOKIE, token, cookieOptions);
        cookies.remove(LOGIN_ACCESS_TOKEN_PRESENCE_COOKIE, { path: '/' });
        logger.auth.debug('loginAccessToken cookie set with token');
        return true;
      }

      if (allowPresenceCookie) {
        setPresenceCookie();
        logger.auth.debug('loginAccessToken presence cookie set (no token yet)');
        return true;
      }

      return false;
    } catch (e) {
      logger.auth.info('Silent token not available at bootstrap (expected in some flows).', e);

      if (allowPresenceCookie && instance.getAllAccounts().length > 0) {
        setPresenceCookie();
        logger.auth.debug('loginAccessToken presence cookie set (after token acquisition failure)');
        return true;
      }

      return false;
    } finally {
      inFlightEnsurePromise = null;
    }
  })();

  return await inFlightEnsurePromise;
};
